import {
    Component, Input, inject
} from "@angular/core";
import { ValidationErrors } from "@angular/forms";

import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { EnhancedControlDirective } from "../../forms/directives/form-control/enhanced-control.directive";
import { IMgdValidationErrorBody } from "../../custom-validators/interfaces/mgd-validation-error-body.interface";
import { ValidationException } from "../../exceptions/validation-exception";

const BUILT_IN_ERROR_MESSAGES: Record<string, (error: any) => string> = {
    required: () => "is required",
    requiredTrue: () => "must be checked",
    email: () => "is not a valid email address",
    minlength: (error) => `must be at least ${error.requiredLength} characters`,
    maxlength: (error) => `must be no more than ${error.requiredLength} characters`,
    min: (error) => `must be at least ${error.min}`,
    max: (error) => `must be no more than ${error.max}`
};

const DEFAULT_PATTERN_MESSAGE = "is not in the correct format";

/**
 * Displays a human-readable error message for a form field controlled by {@link EnhancedControlDirective}.
 *
 * Reads the first active error from the form field and renders its human-readable message.
 * Handles all built-in Angular 19 validator error keys, including `pattern`, and any custom validator
 * that returns an {@link IMgdValidationErrorBody}-shaped error body (`{ message: string }`).
 *
 * Place inside `<mat-error>` so Angular Material controls its visibility:
 *
 * @example
 * ```html
 * <mat-form-field>
 *   <mat-label>Email</mat-label>
 *   <input matInput formControlName="email" mgdEnhancedControl #emailCtrl="mgdEnhancedControl" />
 *   <mat-error><mgd-error-message [formField]="emailCtrl" /></mat-error>
 * </mat-form-field>
 * ```
 *
 * @note
 * A raw `Validators.pattern` / `[pattern]` error renders `patternMessage` when it is set, otherwise
 * "is not in the correct format". A `pattern` error with an {@link IMgdValidationErrorBody} body
 * (from {@link CommonValidators.patternMatcherValidator}) renders its own message instead.
 *
 * ```html
 * <input matInput [(ngModel)]="zip" [pattern]="zipRegex" name="zip" mgdEnhancedControl #zipCtrl="mgdEnhancedControl" />
 * <mat-error><mgd-error-message [formField]="zipCtrl" patternMessage="must be 5 digits" /></mat-error>
 * ```
 *
 * @note
 * This component never throws. An error key it can't map to a message renders "is invalid" and is
 * logged once as a {@link ValidationException} via `errorHandler.handleComponentError`.
 *
 * @note
 * **Password field exception:** `set-password-control.component.html` in common-ng uses a
 * custom multi-error display that shows each password requirement as a checklist and toggles
 * a no-error CSS class as requirements are met in real-time. This is a deliberate UX pattern
 * unique to password entry and requires displaying all errors simultaneously, not just the
 * first. `mgd-error-message` will not be applied here.
 *
 * @selector mgd-error-message
 */
@Component({
    selector: "mgd-error-message",
    templateUrl: "./error-message.component.html"
})
export class ErrorMessageComponent {

    /**
     * The enhanced form control (or controls) whose errors this component will display.
     *
     * **Single control** — apply `mgdEnhancedControl` to the input and expose it via a template
     * reference variable, then bind it directly:
     * ```html
     * <input mgdEnhancedControl #ctrl="mgdEnhancedControl" />
     * <mgd-error-message [formField]="ctrl" />
     * ```
     *
     * **Array of controls** — pass an `Array` for compound inputs such as `mat-date-range-input`.
     * The component iterates the array in order and displays the error from the first control
     * that has one. Only one error is ever shown.
     * ```html
     * <input matStartDate mgdEnhancedControl #startCtrl="mgdEnhancedControl" />
     * <input matEndDate mgdEnhancedControl #endCtrl="mgdEnhancedControl" />
     * <mgd-error-message [formField]="[startCtrl, endCtrl]" />
     * ```
     */
    @Input() formField: EnhancedControlDirective | Array<EnhancedControlDirective>;

    /**
     * Message shown for a raw `Validators.pattern` / `[pattern]` error, which carries no message of
     * its own. Defaults to "is not in the correct format". Ignored for every other error key and for
     * `pattern` errors that already carry an {@link IMgdValidationErrorBody} message.
     */
    @Input() patternMessage: string;

    private readonly _app = inject(COMMON_APP_SERVICE, { optional: true });
    private _lastReportedErrorKey: string | null = null;

    /**
     * Resolves the human-readable message for the first active validation error.
     * When `formField` is an array, iterates controls in order and stops at the first with errors.
     * Returns `null` when no control has errors (renders nothing in the template).
     *
     * The fallback path (unhandled error key) calls `errorHandler.handleComponentError` at most
     * once per unique key so that repeated change detection cycles do not produce duplicate logs.
     */
    get errorMessage(): string | null {

        const fields = Array.isArray(this.formField) ? this.formField : [this.formField];

        for (const field of fields) {

            const errors: ValidationErrors | null = field?.ngControl?.errors ?? null;

            if (!errors) {
                continue;
            }

            const firstKey = Object.keys(errors)[0];
            const errorValue = errors[firstKey];

            if (firstKey in BUILT_IN_ERROR_MESSAGES) {
                this._lastReportedErrorKey = null;
                return BUILT_IN_ERROR_MESSAGES[firstKey](errorValue);
            }

            if (this._isValidationErrorBody(errorValue)) {
                this._lastReportedErrorKey = null;
                return errorValue.message;
            }

            if (firstKey === "pattern") {
                this._lastReportedErrorKey = null;
                return this.patternMessage || DEFAULT_PATTERN_MESSAGE;
            }

            if (firstKey !== this._lastReportedErrorKey) {
                this._lastReportedErrorKey = firstKey;
                const controlName = String(field?.ngControl?.name ?? "unknown");
                const exception = new ValidationException(
                    `Unhandled form validation error key: '${firstKey}' on control '${controlName}'`
                );
                this._app?.errorHandler.handleComponentError(exception);
            }

            return "is invalid";
        }

        this._lastReportedErrorKey = null;
        return null;
    }

    private _isValidationErrorBody(value: unknown): value is IMgdValidationErrorBody {

        return (
            value !== null
            && typeof value === "object"
            && typeof (value as IMgdValidationErrorBody).message === "string"
        );
    }
}
